import * as zod from 'zod';

/**
 * A ShowPath skill's path as it plays: the newest recording cut to the skill's
 * trim window, decimated. Stored on `shared` so lists and the skill page can
 * show it without fetching the recording's frames.
 *
 * `sourceKey` names the inputs it was made from; when the skill's newest
 * recording or trim no longer match it, it is stale. `storageID` is null when
 * those inputs select nothing to draw.
 */
export const ShowPathThumbnail = zod.object({
  sourceKey: zod.string(),
  recordingId: zod.string(),
  storageID: zod.string().nullable(),
  points: zod.array(zod.tuple([zod.number(), zod.number(), zod.number()])),
});

export type ShowPathThumbnail = zod.infer<typeof ShowPathThumbnail>;

const StoredThumbnail = zod
  .object({ pathThumbnail: ShowPathThumbnail })
  .transform(({ pathThumbnail }) => pathThumbnail);

const ShowPathThumbnailInputs = zod.object({
  recordings: zod.array(zod.unknown()).optional().catch(undefined),
  trimStartTimestamp: zod.number().nullable().optional().catch(null),
  trimEndTimestamp: zod.number().nullable().optional().catch(null),
});

/** The newest recording and trim a ShowPath skill's `shared` would play. */
export function showPathThumbnailInputs(shared: unknown): {
  recordingId: string;
  trimStart: number | null;
  trimEnd: number | null;
  sourceKey: string;
} | null {
  const parsed = ShowPathThumbnailInputs.safeParse(shared ?? {});

  if (!parsed.success) return null;

  const recordingId = parsed.data.recordings?.at(-1);

  if (typeof recordingId !== 'string' || recordingId === '') return null;

  const trimStart = parsed.data.trimStartTimestamp ?? null;
  const trimEnd = parsed.data.trimEndTimestamp ?? null;

  return {
    recordingId,
    trimStart,
    trimEnd,
    sourceKey: `${recordingId}|${trimStart ?? ''}|${trimEnd ?? ''}`,
  };
}

/** The stored thumbnail, when it was made from what the skill plays now. */
export function currentShowPathThumbnail(
  shared: unknown,
): ShowPathThumbnail | null {
  const inputs = showPathThumbnailInputs(shared);

  if (!inputs) return null;

  const stored = StoredThumbnail.safeParse(shared ?? {});

  return stored.success && stored.data.sourceKey === inputs.sourceKey
    ? stored.data
    : null;
}

type Vec3 = readonly [number, number, number];

/**
 * The direction the skill page's 3D path view looks from (Z up), so the
 * thumbnail shows the path from the same side.
 */
export const SHOW_PATH_VIEW_DIRECTION: readonly [number, number, number] = [
  0.7, -0.9, 0.5,
];

// 16:9 to fill a skills-grid card; the path is fitted inside the centred 4:3
// region so a 4:3 crop (the step picker's thumbnail) keeps all of it.
const WIDTH = 192;
const HEIGHT = 108;
const FIT_WIDTH = 144;
const PADDING = 14;
const STROKE = '#EF4444';
/** A path whose bounding-box diagonal is under this is a stationary take. */
export const STATIONARY_EXTENT_M = 1e-3;

/** Whether a path is a stationary take, drawn as a point. */
export function isStationaryPath(points: ReadonlyArray<Vec3>): boolean {
  if (points.length <= 1) return true;

  const extent = [0, 1, 2].map(
    (axis) =>
      Math.max(...points.map((p) => p[axis]!)) -
      Math.min(...points.map((p) => p[axis]!)),
  );

  return Math.hypot(extent[0]!, extent[1]!, extent[2]!) < STATIONARY_EXTENT_M;
}

const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

const normalize = (a: Vec3): Vec3 => {
  const length = Math.hypot(a[0], a[1], a[2]);

  return [a[0] / length, a[1] / length, a[2] / length];
};

/** Screen axes of a camera looking along -direction with Z up. */
function screenAxes(direction: Vec3): { right: Vec3; up: Vec3 } {
  const forward = normalize([-direction[0], -direction[1], -direction[2]]);
  const right = normalize(cross(forward, [0, 0, 1]));

  return { right, up: cross(right, forward) };
}

const round = (value: number) => Math.round(value * 10) / 10;

/**
 * The path drawn as an SVG, from `SHOW_PATH_VIEW_DIRECTION`. Null when there
 * is nothing to draw.
 */
export function drawShowPathSvg(points: ReadonlyArray<Vec3>): string | null {
  if (points.length === 0) return null;

  const { right, up } = screenAxes(SHOW_PATH_VIEW_DIRECTION);
  const projected = points.map((p) => [dot(p, right), -dot(p, up)] as const);

  const xs = projected.map(([x]) => x);
  const ys = projected.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const spanX = Math.max(...xs) - minX;
  const spanY = Math.max(...ys) - minY;

  const open = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" width="${WIDTH}" height="${HEIGHT}"><rect width="${WIDTH}" height="${HEIGHT}" fill="#FFFFFF"/>`;

  if (isStationaryPath(points)) {
    return `${open}<circle cx="${WIDTH / 2}" cy="${HEIGHT / 2}" r="5" fill="${STROKE}"/></svg>`;
  }

  const boxWidth = FIT_WIDTH - 2 * PADDING;
  const boxHeight = HEIGHT - 2 * PADDING;

  // A flat axis does not limit the scale; the other axis fills the box.
  const fit = (box: number, span: number) =>
    span > 0 ? box / span : Number.POSITIVE_INFINITY;

  const scale = Math.min(fit(boxWidth, spanX), fit(boxHeight, spanY));
  const offsetX = (WIDTH - spanX * scale) / 2;
  const offsetY = (HEIGHT - spanY * scale) / 2;

  const screen = projected.map(
    ([x, y]) =>
      [
        round(offsetX + (x - minX) * scale),
        round(offsetY + (y - minY) * scale),
      ] as const,
  );

  const polyline = screen.map(([x, y]) => `${x},${y}`).join(' ');
  const [startX, startY] = screen[0]!;
  const [endX, endY] = screen[screen.length - 1]!;

  return (
    `${open}<polyline points="${polyline}" fill="none" stroke="${STROKE}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` +
    `<circle cx="${startX}" cy="${startY}" r="4" fill="#FFFFFF" stroke="${STROKE}" stroke-width="2"/>` +
    `<circle cx="${endX}" cy="${endY}" r="4" fill="${STROKE}"/></svg>`
  );
}
